package com.kaihuoy.web.router

import com.kaihuoy.home.screens.AdminScreen
import androidx.compose.runtime.*
import com.kaihuoy.home.MainDashboardScreen
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.User
import com.kaihuoy.components.common.*

// Tuo kaikki screenit
import com.kaihuoy.home.screens.*
import com.kaihuoy.components.common.*

// ======================
// Yksinkertainen reititystila
// ======================

object AppRouterState {
    var currentPath by mutableStateOf(pathFromUrl())
    var pendingHighlightId: String? = null

    fun navigate(path: String, highlightId: String? = null) {
        pendingHighlightId = highlightId
        currentPath = path
        val newUrl = if (path == "/" || path == "") "/" else "#/$path"
        window.history.pushState(null, "", newUrl)
    }

    fun syncFromUrl() {
        currentPath = pathFromUrl()
    }

    private fun pathFromUrl(): String {
        val hash = window.location.hash
        return if (hash.startsWith("#/")) hash.removePrefix("#/") else "/"
    }
}

// ======================
// AppRouter
// ======================

@Composable
fun AppRouter(user: User?, isAdmin: Boolean, onLogout: () -> Unit) {
    val isPutkityo = user?.isPutkityo == true
    DisposableEffect(Unit) {
        val listener: (dynamic) -> Unit = { AppRouterState.syncFromUrl() }
        window.addEventListener("popstate", listener)
        onDispose { window.removeEventListener("popstate", listener) }
    }

    val currentPath = AppRouterState.currentPath

    val isDesktopView = rememberIsDesktop()
    var isMobileMenuOpen by remember { mutableStateOf(false) }

    LaunchedEffect(currentPath) {
        isMobileMenuOpen = false
    }

    if (isDesktopView) {
        Div(attrs = { style { display(DisplayStyle.Flex); minHeight(100.vh) } }) {
            Div(attrs = { classes("no-print") }) {
                AppSidebar(user, isAdmin, isPutkityo, currentPath)
            }
            
            Div(attrs = {
                style {
                    flexGrow(1)
                    width(100.percent)
                    backgroundColor(KaihuTheme.Background)
                    height(100.vh)
                    overflowY("auto")
                }
            }) {
                AppRoutes(user, isAdmin, isPutkityo, onLogout, currentPath)
            }
        }
    } else {
        Div(attrs = {
            style {
                minHeight(100.vh)
                position(Position.Relative)
                backgroundColor(KaihuTheme.Background)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
            }
        }) {
            val isHome = currentPath.removePrefix("/").removePrefix("#/").let { it.isBlank() || it == "/" || it == "etusivu" || it == "home" }
            if (!isHome) {
                MobileHeader(user, currentPath)
            }
            Div(attrs = { style { flex(1) } }) {
                AppRoutes(user, isAdmin, isPutkityo, onLogout, currentPath)
            }
            MobileNavigationOverlay(
                isAdmin = isAdmin,
                isPutkityo = isPutkityo,
                onLogout = onLogout,
                currentPath = currentPath,
                isOpen = isMobileMenuOpen,
                onToggle = { isMobileMenuOpen = !isMobileMenuOpen },
                onClose = { isMobileMenuOpen = false }
            )
        }
    }
}

@Composable
private fun MobileHeader(user: User?, currentPath: String) {
    val cleanPath = currentPath.removePrefix("/").removePrefix("#/")
    val title = when (cleanPath) {
        "perehdytys" -> "Perehdytys"
        "tarkastus", "inspection" -> "Kaluston tarkastus"
        "hallinta" -> "Hallinta"
        "huolto", "service" -> "Huoltokirjaus"
        "huoltoilmoitukset", "service_calendar" -> "Huoltoilmoitukset"
        "konehuollot", "machine_maintenance" -> "Konehuollot"
        "koneet" -> "Koneet"
        "kuormakirja" -> "Kuormakirja"
        "profiili", "profile" -> "Profiili"
        "uusi_perehdytys", "new_induction" -> "Uusi perehdytys"
        "uusi_tarkastus", "new_inspection" -> "Uusi tarkastus"
        "uusi_huolto", "new_service" -> "Uusi huoltokirjaus"
        "omat_kirjaukset" -> "Omat kirjaukset"
        "painekoe" -> "Putkityö"
        else -> "KaihuOY"
    }

    Div(attrs = {
        classes("no-print")
        style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center); padding(12.px, 20.px)
            backgroundColor(KaihuTheme.Surface)
            property("border-bottom", "1px solid ${KaihuTheme.Divider}")
            property("flex-shrink", "0")
        }
    }) {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
            Span(attrs = {
                onClick { AppRouterState.navigate("/") }
                style {
                    color(KaihuTheme.PrimaryTeal); fontSize(1.3.em); cursor("pointer")
                    property("line-height", "1")
                }
            }) { Text("←") }
            Span(attrs = { style { color(KaihuTheme.TextPrimary); fontWeight("700"); fontSize(1.05.em) } }) {
                Text(title)
            }
        }
        Div(attrs = {
            onClick { AppRouterState.navigate("profiili") }
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
                padding(6.px, 12.px); borderRadius(12.px); cursor("pointer")
                property("border", "1px solid ${KaihuTheme.Border}")
                property("background", KaihuTheme.CardGradient)
            }
        }) {
            Div(attrs = {
                style {
                    width(28.px); height(28.px); borderRadius(50.percent)
                    backgroundColor(KaihuTheme.PrimaryTeal)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    color(KaihuTheme.Background); fontWeight("800"); fontSize(0.85.em)
                }
            }) { Text(user?.displayName?.firstOrNull()?.uppercaseChar()?.toString() ?: "?") }
            Span(attrs = { style { color(KaihuTheme.TextPrimary); fontSize(0.85.em); fontWeight("600") } }) {
                Text(user?.displayName?.split(" ")?.firstOrNull() ?: "Profiili")
            }
        }
    }
}

// ======================
// Reitit
// ======================

@Composable
private fun AppRoutes(
    user: User?,
    isAdmin: Boolean,
    isPutkityo: Boolean,
    onLogout: () -> Unit,
    currentPath: String
) {
    val cleanPath = currentPath.removePrefix("/").removePrefix("#/")

    when (cleanPath) {
        "", "etusivu", "home", "/" -> MainDashboardScreen(user, isAdmin, onLogout = onLogout)
        "perehdytys" -> InductionScreen(user, isAdmin, onLogout = onLogout)
        "tarkastus", "inspection" -> InspectionScreen(user, onLogout = onLogout)
        "hallinta" -> AdminScreen(user, isAdmin, onLogout = onLogout, accentHex = "#EF4444")
        "huolto", "service" -> ServiceScreen(user, onLogout = onLogout)
        "huoltoilmoitukset", "service_calendar" -> MaintenanceCalendarScreen(user, isAdmin)
        "konehuollot", "machine_maintenance" -> MachineMaintenanceScreen(user, isAdmin)
        "koneet" -> com.kaihuoy.home.screens.MachineScreen(user, isAdmin, standalone = true, onLogout = onLogout)
        "kuormakirja" -> KuormakirjaFrame(user, onLogout = onLogout)
        "profiili", "profile" -> ProfileScreen(user)

        "uusi_perehdytys", "new_induction" -> NewInductionScreen(user, isAdmin)
        "uusi_tarkastus", "new_inspection" -> NewInspectionScreen(user)
        "uusi_huolto", "new_service" -> NewServiceScreen(user)
        "omat_kirjaukset" -> OmatKirjauksetScreen(user)
        "painekoe" -> if (isAdmin || isPutkityo) {
            PainekoeScreen(user, isAdmin, onLogout)
        } else {
            Div(attrs = { style { padding(60.px) } }) { H2 { Text("Ei käyttöoikeutta") } }
        }

        else -> Div(attrs = { style { padding(60.px) } }) {
            H2 { Text("Hups — sivua ei löydy") }
            Text("Tarkista osoite: $currentPath")
        }
    }
}

// ======================
// Sivupalkki
// ======================


@Composable
fun AppSidebar(user: User?, isAdmin: Boolean, isPutkityo: Boolean = false, currentPath: String) {
    Div(attrs = {
        style {
            padding(12.px, 0.px, 12.px, 12.px)
            position(Position.Sticky)
            top(0.px)
            height(100.vh)
            boxSizing("border-box")
            property("flex-shrink", "0")
        }
    }) {
    Div(attrs = {
        style {
            width(276.px)
            height(100.percent)
            backgroundColor(KaihuTheme.Surface)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            padding(24.px)
            boxSizing("border-box")
            borderRadius(20.px)
            property("box-shadow", "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)")
            property("border", "1px solid ${KaihuTheme.Border}")
            property("background", "linear-gradient(160deg, #1e2235 0%, #131625 100%)")
        }
    }) {
        H1(attrs = {
            style {
                margin(0.px, 0.px, 40.px, 0.px)
                color(KaihuTheme.PrimaryTeal)
                fontWeight("800")
                cursor("pointer")
                fontSize(1.4.em)
            }
            onClick { AppRouterState.navigate("/") }
        }) {
            Text("KaihuOY")
        }

        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
                flexGrow(1)
                overflowY("auto")
            }
        }) {
            navigationItems(isAdmin, isPutkityo).forEach { item ->
                SidebarItem(item.title, item.icon, item.path, currentPath)
            }
        }

        // Profiilinappi
        Div(attrs = {
            onClick { AppRouterState.navigate("profiili") }
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px)
                padding(12.px, 14.px); borderRadius(14.px); cursor("pointer")
                property("border", "1px solid ${KaihuTheme.Border}")
                property("background", KaihuTheme.CardGradient)
                property("transition", "border-color 0.15s")
            }
        }) {
            Div(attrs = {
                style {
                    width(36.px); height(36.px); borderRadius(50.percent)
                    backgroundColor(KaihuTheme.PrimaryTeal)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    fontWeight("800"); fontSize(0.95.em); color(KaihuTheme.Background)
                    property("flex-shrink", "0")
                }
            }) {
                Text(user?.displayName?.firstOrNull()?.uppercaseChar()?.toString() ?: "?")
            }
            Div(attrs = { style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); overflow("hidden") } }) {
                Span(attrs = {
                    style {
                        color(KaihuTheme.TextPrimary); fontWeight("600"); fontSize(0.9.em)
                        property("white-space", "nowrap"); property("overflow", "hidden"); property("text-overflow", "ellipsis")
                    }
                }) { Text(user?.displayName ?: "Käyttäjä") }
                Span(attrs = { style { color(KaihuTheme.TextLight); fontSize(0.75.em) } }) {
                    Text(when {
                        user?.isAdmin == true -> "Ylläpitäjä"
                        user?.isHuolto == true -> "Huolto"
                        user?.isPutkityo == true -> "Putkityö"
                        else -> "Käyttäjä"
                    })
                }
            }
        }
    }
    }
}

private data class NavigationItem(
    val title: String,
    val icon: String,
    val path: String
)

private fun navigationItems(isAdmin: Boolean, isPutkityo: Boolean = false): List<NavigationItem> = buildList {
    add(NavigationItem("Etusivu", "home", "etusivu"))
    add(NavigationItem("Perehdytys", "circle-check", "perehdytys"))
    add(NavigationItem("Kaluston tarkastus", "tool", "tarkastus"))
    add(NavigationItem("Huoltokirjaus", "engine", "huolto"))
    add(NavigationItem("Koneet", "tractor", "koneet"))
    if (isAdmin || isPutkityo) {
        add(NavigationItem("Painekoe", "droplet", "painekoe"))
    }
    add(NavigationItem("Kuormakirja", "package", "kuormakirja"))
    add(NavigationItem(if (isAdmin) "Hallinta" else "Omat kirjaukset", if (isAdmin) "settings" else "clipboard-list", "hallinta"))
}

@Composable
private fun MobileNavigationOverlay(
    isAdmin: Boolean,
    isPutkityo: Boolean = false,
    onLogout: () -> Unit,
    currentPath: String,
    isOpen: Boolean,
    onToggle: () -> Unit,
    onClose: () -> Unit
) {
    if (isOpen) {
        Div(attrs = {
            onClick { onClose() }
            style {
                position(Position.Fixed)
                top(0.px)
                right(0.px)
                bottom(0.px)
                left(0.px)
                backgroundColor(rgba(15, 23, 42, 0.55))
                property("z-index", "999")
            }
        })

        Div(attrs = {
            style {
                position(Position.Fixed)
                left(16.px)
                right(16.px)
                bottom(88.px)
                backgroundColor(KaihuTheme.Surface)
                borderRadius(20.px)
                padding(16.px)
                property("box-shadow", KaihuTheme.ShadowLg)
                property("z-index", "1000")
                maxHeight(70.vh)
                overflowY("auto")
            }
        }) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(12.px)
                }
            }) {
                H3(attrs = { style { margin(0.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("Valikko")
                }
                StyledButton(
                    text = "Sulje",
                    onClick = onClose,
                    backgroundColor = KaihuTheme.Grey
                )
            }

            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                    marginBottom(12.px)
                }
            }) {
                navigationItems(isAdmin, isPutkityo).forEach { item ->
                    SidebarItem(item.title, item.icon, item.path, currentPath, onNavigate = onClose)
                }
            }

            StyledButton(
                text = "Kirjaudu ulos 🚪",
                onClick = {
                    onClose()
                    onLogout()
                },
                backgroundColor = KaihuTheme.Background
            )
        }
    }

    Button(attrs = {
        onClick { onToggle() }
        style {
            position(Position.Fixed)
            right(16.px)
            bottom(16.px)
            width(60.px)
            height(60.px)
            borderRadius(999.px)
            border(0.px)
            backgroundColor(KaihuTheme.PrimaryTeal)
            color(Color.white)
            fontSize(1.6.em)
            property("box-shadow", KaihuTheme.ShadowLg)
            property("z-index", "1001")
            cursor("pointer")
        }
    }) {
        Text(if (isOpen) "✕" else "☰")
    }
}

@Composable
private fun SidebarItem(
    title: String,
    icon: String,
    path: String,
    currentPath: String,
    onNavigate: () -> Unit = {}
) {
    val cleanPath = currentPath.removePrefix("/").removePrefix("#/")
    val isSelected = cleanPath == path ||
                     (path == "etusivu" && (cleanPath.isBlank() || cleanPath == "/" || cleanPath == "home")) ||
                     (path == "hallinta" && cleanPath == "admin")
    var hovered by remember { mutableStateOf(false) }

    Div(attrs = {
        onClick {
            if (path.startsWith("http")) {
                window.open(path, "_blank")
            } else {
                AppRouterState.navigate(path)
            }
            onNavigate()
        }
        onMouseOver { hovered = true }
        onMouseOut { hovered = false }
        style {
            padding(16.px)
            borderRadius(12.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(16.px)
            cursor("pointer")
            backgroundColor(if (isSelected) KaihuTheme.PrimaryTeal else Color.transparent)
            color(if (isSelected) Color.white else KaihuTheme.TextPrimary)
            fontWeight(if (isSelected) "bold" else "600")
            property("border", if (!isSelected && hovered) "1px solid ${KaihuTheme.PrimaryTeal}" else "1px solid transparent")
            property("box-shadow", if (!isSelected && hovered) "0 0 10px rgba(34,211,238,0.18)" else "none")
            property("transition", "all 0.18s")
        }
    }) {
        TIcon(icon, 1.4.em)
        Span { Text(title) }
    }
}

@Composable
private fun rememberIsDesktop(): Boolean {
    var isDesktop by remember { mutableStateOf(window.innerWidth > 900) }

    DisposableEffect(Unit) {
        val listener: (dynamic) -> Unit = { isDesktop = window.innerWidth > 900 }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }
    return isDesktop
}

@Composable
private fun KuormakirjaFrame(user: User?, onLogout: () -> Unit) {
    PageLayout(greeting = getGreeting(user?.displayName), onLogout = onLogout) {
    Div(attrs = {
        style {
            width(100.percent)
            height(100.vh)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {

        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                padding(8.px, 16.px)
                backgroundColor(KaihuTheme.Surface)
                property("border-bottom", "1px solid ${KaihuTheme.Divider}")
                property("flex-shrink", "0")
            }
        }) {
            Span(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary) } }) {
                Text("📦 Kuormakirja")
            }
            A(href = "https://kuormakirja.web.app/", attrs = {
                attr("target", "_blank")
                attr("rel", "noopener noreferrer")
                style {
                    fontSize(0.85.em)
                    color(KaihuTheme.PrimaryTeal)
                    textDecoration("none")
                }
            }) {
                Text("Avaa uuteen välilehteen ↗")
            }
        }
        Iframe(attrs = {
            attr("src", "https://kuormakirja.web.app/")
            attr("allow", "clipboard-read; clipboard-write")
            style {
                width(100.percent)
                flex(1)
                border(0.px)
                property("flex-grow", "1")
            }
        })
    }
}}